/**
 * Folder deep-linking. Lets a URL land a visitor directly in a folder, e.g.
 *   https://mktgassets.moldex.com/?folder=Enhanced%20Digital%20Content
 * Also accepts a hash form (#folder=… or a bare #<path>) for convenience.
 *
 * The folder string is an albumPath PREFIX — assetMatchesPath() shows that node
 * and its whole subtree (so "Enhanced Digital Content" includes everything under
 * it, but not the separate "Enhanced Digital Content AUS NZ").
 */
export function readFolderFromUrl(): string {
  try {
    if (typeof window === "undefined") return "";
    const q = new URLSearchParams(window.location.search).get("folder");
    if (q) return q;
    const h = window.location.hash.replace(/^#/, "");
    if (!h) return "";
    if (h.toLowerCase().startsWith("folder=")) return decodeURIComponent(h.slice("folder=".length));
    return decodeURIComponent(h);
  } catch {
    return "";
  }
}

/**
 * Locked-folder mode: `?only=<albumPath>` roots the whole picker at that folder.
 * The tree, breadcrumb, search and filters are all confined to it and there's no
 * UI path out. NOTE: this is a UX guardrail, NOT a security boundary — the full
 * manifest still loads client-side and /cdn is public, so it only steers normal
 * users. For true isolation an EDC-only manifest/route would be required.
 */
export function readLockedFolder(): string {
  try {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("only") ?? "";
  } catch {
    return "";
  }
}

/** Reflect the current folder into the URL (?folder=…) so the location is shareable. */
export function writeFolderToUrl(folderPath: string): void {
  try {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (folderPath) url.searchParams.set("folder", folderPath);
    else url.searchParams.delete("folder");
    // Drop any legacy hash form so the two don't fight.
    if (url.hash.replace(/^#/, "").toLowerCase().startsWith("folder=")) url.hash = "";
    window.history.replaceState(null, "", url.toString());
  } catch {
    /* non-fatal */
  }
}
